import { apiService } from './api';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'analyst' | 'viewer';
  permissions: string[];
  avatar?: string;
  createdAt: Date;
  lastLogin?: Date;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  role?: 'admin' | 'analyst' | 'viewer';
}

class AuthService {
  private currentUser: User | null = null;

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/auth/login', credentials);

    // Store tokens
    localStorage.setItem('authToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);

    // Store user data
    this.currentUser = response.user;
    localStorage.setItem('user', JSON.stringify(response.user));

    return response;
  }

  async register(data: RegisterData): Promise<User> {
    const user = await apiService.post<User>('/auth/register', data);
    return user;
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      this.currentUser = null;

      // Redirect to login
      window.location.href = '/login';
    }
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiService.post<{ accessToken: string; expiresIn: number }>(
      '/auth/refresh',
      { refreshToken }
    );

    localStorage.setItem('authToken', response.accessToken);
    return response.accessToken;
  }

  async getCurrentUser(): Promise<User | null> {
    if (this.currentUser) {
      return this.currentUser;
    }

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      this.currentUser = JSON.parse(storedUser);
      return this.currentUser;
    }

    try {
      const user = await apiService.get<User>('/auth/me');
      this.currentUser = user;
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      return null;
    }
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const user = await apiService.put<User>('/auth/profile', data);
    this.currentUser = user;
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiService.post('/auth/change-password', { oldPassword, newPassword });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await apiService.post('/auth/reset-password-request', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiService.post('/auth/reset-password', { token, newPassword });
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }

  hasPermission(permission: string): boolean {
    return this.currentUser?.permissions.includes(permission) || false;
  }

  hasRole(role: string): boolean {
    return this.currentUser?.role === role;
  }
}

export const authService = new AuthService();